import { get as _get } from 'lodash'
import {
  ConfigurationDetail,
  DatadogEncodingRun,
  EncodingCounters,
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  IdentifierType,
  ItemConfigurations,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrderRowDetail,
  Products,
  TmrTag
} from 'stylewhere/api'
import { __, T } from 'stylewhere/i18n'
import { AppStore, FormSchema, FormSchemaData, jsonToFormSchema, OperationConfig, Router } from 'stylewhere/shared'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  enabledWamGroupedProduct,
  getEncodingBlockedErrors,
  getInitialType,
  isEncodingErrorCheckingByRole,
  isExpectOnlyMandatoryIdentifiers
} from 'stylewhere/shared/utils'
import { Extensions } from './Extensions'

export type OverrideProductInfoFieldsContext = 'encodingForm'

export interface GetProductionOrderDetails {
  productionOrderRow?: ProductionOrderRow
  encodingValidationResponse?: EncodingValidationResponse
}

export class EncodingExtensions extends Extensions {
  static async beforeConfirm<O extends any[]>(
    operation: OperationConfig,
    data: FormSchemaData,
    // Possono essere una lista di items, di parcels o altro a seconda del tipo di operazione
    objects: O
  ): Promise<boolean> {
    return true
  }

  static formSchema(operation: EncodingOperationConfig, additionalData?: any, filterKey?: string[]): FormSchema {
    if (!operation) return []
    const initialType = getInitialType(operation)
    if (initialType === 'formSchema' && operation.formSchema && Object.keys(operation.formSchema).length > 0)
      return jsonToFormSchema(operation.formSchema as any)

    if (operation.templatePath && operation.templatePath === 'encodingOutboundSap') {
      return []
    }

    if (initialType === 'product') {
      return [
        {
          type: 'text',
          name: 'product.code',
          label: __(T.misc.upc),
          required: true,
          autoFocus: true
        }
      ]
    }

    if (initialType === 'wam') {
      return [
        {
          type: 'text',
          name: 'wam',
          label: __(T.custom.WAM),
          required: true,
          autoFocus: true
        }
      ]
    }

    if (initialType === 'order') return this.getOrderFormSchema(operation, additionalData, true)

    if (initialType === 'orderRow') return this.getOrderFormSchema(operation, additionalData, false)

    return []
  }

  static getOrderFormSchema(
    operation: EncodingOperationConfig,
    additionalData: any = {},
    showProductionOrder: boolean = false
  ): FormSchema {
    const mode =
      operation.options && operation.options.productionOrderRowSelectionMode
        ? operation.options.productionOrderRowSelectionMode
        : 'byOrderAndRowCode'

    const formSchema: FormSchema = []
    if (showProductionOrder)
      formSchema.push({
        type: 'text',
        name: 'productionOrder',
        label: __(T.misc.production_order),
        required: !additionalData.productionOrder,
        autoFocus: !additionalData.productionOrder,
        hide: !!additionalData.productionOrder
      })

    formSchema.push({
      type: 'text',
      name: 'productionOrderRow',
      label: mode === 'byOrderAndRowCode' ? __(T.misc.production_order_row) : __(T.misc.production_order_sku),
      required: !!additionalData.productionOrder || !showProductionOrder,
      autoFocus: !!additionalData.productionOrder || !showProductionOrder,
      hide: !additionalData.productionOrder && showProductionOrder
    })

    return formSchema
  }

  static async getProductId(productCode, operationId) {
    const encodingProductsResult = await Encodings.findProduct({ code: productCode, operationId })
    if (encodingProductsResult.ok) {
      return encodingProductsResult.data?.id
    } else if (encodingProductsResult.status !== 400) {
      const products = await Products.searchCodes([productCode])
      if (products.content?.[0]?.id) {
        return products.content[0].id
      }
    }

    throw new Error(__(T.error.product_not_found))
  }

  static getDataDogFormSchemaLabel = (
    operation: EncodingOperationConfig,
    data: FormSchemaData,
    initialType: string
  ) => {
    let label = ''
    if (initialType === 'identifier' || initialType === 'identifierOrderRow') {
      label = _get(data, 'identifier') ?? ''
    } else if (initialType === 'formSchema') {
      const schema = EncodingExtensions.formSchema(operation, data)
      const find = schema.find((el) => el.isProductCode)
      if (find) {
        label = _get(data, find.name) ?? ''
      }
    } else if (initialType === 'product') {
      label = data.product.code
    } else if (initialType === 'order' || initialType === 'orderRow') {
      label = data.productionOrderRow.code
    } else if (initialType === 'wam') {
      label = data.wam
    }
    return label
  }

  static getItemTypeConfiguration = async (operation: EncodingOperationConfig) => {
    let configuration = undefined
    let itemConfig = ''
    let itemConfigDefault = ''
    const keys = Object.keys(operation.options)
    let key
    for (let k = 0; k < keys.length; k++) {
      if (keys[k].indexOf('itemTypeConfig_') !== -1) {
        key = keys[k].replace('itemTypeConfig_', '')
        if (key === 'default') {
          itemConfigDefault = operation.options[keys[k]] || ''
        } else if (itemConfig === '') {
          itemConfig = operation.options[keys[k]] || ''
        }
      }
    }
    if (itemConfig !== '' || itemConfigDefault !== '') {
      const res: any = await ItemConfigurations.search({
        equalCodes: itemConfig !== '' ? itemConfig : itemConfigDefault
      })
      if (res && res.content && res.content.length > 0 && res.content[0].details && res.content[0].details.length > 0) {
        configuration = res.content[0].details
      }
    }
    return configuration
  }

  static async getItemConfiguration(
    operation: EncodingOperationConfig,
    data: FormSchemaData,
    operationAttributes?: any
  ) {
    try {
      const initialType = getInitialType(operation)
      const encodingValidation: EncodingValidationRequest = {
        identifiers: [],
        operationId: operation.id,
        placeId: AppStore.defaultWorkstation!.placeId,
        workstationId: AppStore.defaultWorkstation!.id,
        operationAttributes: operationAttributes || {}
      }
      if (initialType === 'formSchema') {
        const schema = EncodingExtensions.formSchema(operation, data)
        const find = schema.find((el) => el.isProductCode)
        if (find) {
          encodingValidation.startEntityId = await this.getProductId(_get(data, find.name), operation.id)
        } else throw new Error(__(T.error.no_item_configuration))
      } else if (initialType === 'product') {
        encodingValidation.startEntityId = await this.getProductId(data.product.code, operation.id)
      } else if (initialType === 'order' || initialType === 'orderRow') {
        encodingValidation.startEntityId = data.productionOrderRow.id
      } else if (initialType === 'wam') {
        encodingValidation.startCode = data.wam
      }
      const res = await Encodings.validate(encodingValidation)
      if (res) {
        let configurationDetails
        if (res.configurationDetails) configurationDetails = res.configurationDetails
        else if (res.configuration && res.configuration.details) configurationDetails = res.configuration.details
        if (!configurationDetails) configurationDetails = []

        if (configurationDetails.length === 0) {
          const missingOptionError = res.errors.find((e) => e.errorCode === 'Missing Operation Option')
          if (missingOptionError) {
            throw new Error(__(T.error.missing_operation_option, { error: missingOptionError.ref }))
          }
          throw new Error(__(T.error.no_item_configuration))
        }

        const blockedError = res.errors.find((e) =>
          getEncodingBlockedErrors(operation.options.enableUhfWrite).includes(e.errorCode)
        )
        if (blockedError) {
          throw new Error(
            __(
              T.error[blockedError.errorCode],
              blockedError.errorCode === 'MISSING_EAN_ATTRIBUTE' ? { productCode: res.item?.product?.code ?? ' ' } : {}
            )
          )
        }

        let counters: EncodingCounters | undefined
        if (['order', 'orderRow'].includes(initialType) && !!encodingValidation.startEntityId) {
          counters = (await Encodings.getProductionOrderRowCounter(encodingValidation.startEntityId)).data
        }

        const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(operation)
        configurationDetails.forEach((configDetail) => {
          if (!expectOnlyMandatoryIdentifiers || (expectOnlyMandatoryIdentifiers && !configDetail.optional)) {
            encodingValidation.identifiers.push({ ...configDetail })
          }
        })
        return { encodingValidationResponse: res, encodingValidation, counters }
      } else {
        throw new Error(__(T.error.no_item_configuration))
      }
    } catch (error) {
      throw new Error((error as any)?.message ?? __(T.error.no_item_configuration))
    }
  }

  // set identifiers status with association by group
  static async set_identifiers_status(
    encodingValidation: EncodingValidationRequest,
    result?: any,
    enabledUhfWrite?: boolean
  ) {
    encodingValidation.identifiers
      .filter((idf) => !!idf.code)
      .forEach((idf) => {
        idf._status = 'CONFIRMED'
        idf._error = undefined
      })
    if (result && result.item && result.item.identifiers) {
      encodingValidation.identifiers
        // check for enabledUhfWrite can be removed when readingRequired flag is set correctly for all environments
        .filter((f) =>
          enabledUhfWrite ? f.identifierType === 'UHF_TAG' : (f.code && f.code !== '') || f._possibleCodes?.length > 0
        )
        .forEach((idf) => {
          // define roles for wrong UHF values
          const roles = ['UHF', 'UHF_TAG'].includes(idf.role) ? ['UHF', 'UHF_TAG'] : [idf.role]

          if (idf._possibleCodes?.length > 0) {
            // now promote possible code to actual code based on backend assigned role if found ...
            const findRoleIdentifier = result.item.identifiers.find(
              (idt) => idf._possibleCodes?.some((codeObj) => codeObj.code === idt.code) && idt.role === idf.role
            )
            if (findRoleIdentifier) {
              idf.code = findRoleIdentifier.code
              idf.tid = findRoleIdentifier.tid || ''
              idf._possibleCodes = undefined
              idf._status = 'CONFIRMED'
              idf._error = undefined
              // ... and remove that possible code on all other identifiers
              encodingValidation.identifiers.forEach((idft) => {
                if (!idft.code && idft._possibleCodes?.some((codeObj) => codeObj.code === findRoleIdentifier.code)) {
                  idft._possibleCodes = idft._possibleCodes.filter(
                    (codeObj) => codeObj.code !== findRoleIdentifier.code
                  )
                  // if no possible codes are left for this identifier we can surely say that it was not the correct one
                  // based on the role, so we can put it back to its initial state
                  if (idft._possibleCodes?.length === 0 && idft._status === 'PROCESSING') {
                    idft._status = 'TO_BE_READ'
                  }
                }
              })
            }
          }

          // find identifier in validate response for same code, role and identifierType
          const find = result.item.identifiers.find(
            (idt) => idt.code === idf.code && roles.includes(idt.role) && idt.type === idf.identifierType
          )
          if (find && !!find.group) {
            // find identifier in validate response for same group and differente code and type
            let group = result.item.identifiers.find(
              (idt) => idt.group === find.group && idt.code !== idf.code && idt.type !== find.type
            )
            if (!group) {
              //search identifier with same group and type but different role
              group = result.item.identifiers.find(
                (idt) =>
                  idt.group === find.group && idt.code !== idf.code && idt.type === find.type && idt.role !== find.role
              )
            }
            if (group) {
              // find configuration for identifier with same identifierType and role
              const findConfiguration = encodingValidation.identifiers.find(
                (f) => f.identifierType === group.type && f.role === group.role
              )

              // use group only if readingRequired is false
              if (!findConfiguration?.readingRequired) {
                //check if already exist identifier read with same identifierType and role
                const exist = encodingValidation.identifiers.filter(
                  (idft) =>
                    idft.identifierType === group.type &&
                    idft.code !== group.code &&
                    idft.role === group.role &&
                    idft._read &&
                    idft._status !== 'ERROR'
                )
                if (exist.length > 0) {
                  // add a extra tag
                  encodingValidation.identifiers.push({
                    identifierType: group.type,
                    role: exist[0].role ?? '',
                    code: exist[0].code ?? '',
                    _read: true,
                    _status: 'ERROR',
                    _error: 'Extra tag'
                  })
                }
                // update code for identifier with same identifierType
                encodingValidation.identifiers
                  .filter((idft) => idft.identifierType === group.type && idft._status !== 'ERROR')
                  .forEach((idft) => {
                    idft._status = 'CONFIRMED'
                    idft._error = undefined
                    idft.code = group.code
                  })
              }
            }
          }
        })
    }

    if (
      result &&
      result.item &&
      result.item.attributes &&
      result.item.attributes.ignoredTags &&
      result.item.attributes.ignoredTags !== ''
    ) {
      const ignoredTags = result.item.attributes.ignoredTags.split(',')
      if (ignoredTags.length > 0) {
        ignoredTags.forEach((tag) => {
          const index = encodingValidation.identifiers.findIndex((idf) => idf.code === tag.trim())
          if (index >= 0) {
            encodingValidation.identifiers[index]._status = 'IGNORED'
          }
        })
      }
    }
    if (result && result.errors) {
      let errorStr
      result.errors.forEach((error) => {
        switch (error.errorCode) {
          case 'Tag Mismatch': {
            encodingValidation.identifiers.forEach((idf) => {
              idf._status = 'ERROR'
              idf._error = T.identifier_error[error.errorCode]
                ? __(T.identifier_error[error.errorCode])
                : error.errorCode
            })
            break
          }
          // errors in which error.ref can point to an identifier not present in result.item.identifiers
          case 'UNEXPECTED_TAG':
          case 'EXTRA_TAG': {
            // We know that, logically, the extra tag should not be the one that we've already processed
            // regardless of the reading order, this means that error.ref is not reliable as a reference to the extra tag.
            // since backend validate request management is stateless, it always sends the same identifier as the extra one,
            const referenceIdentifier = encodingValidation.identifiers.find((idf) => idf.code === error.ref)
            if (referenceIdentifier) {
              // bad case: the backend set the extra tag as one of the already read ones.
              // Keep the already read one and set the extra ones as error.
              // If we already have an identifier with the same type and role but no code, we can set it to extra tag,
              // if instead we don't have any identifier with the same type and role we can add a new one as error (if not already read).
              result.item.identifiers.forEach((idt) => {
                const identifierToReplace = encodingValidation.identifiers.find(
                  (idf) => !idf.code && idf.identifierType === idt.type && idf.role === idt.role
                )
                if (identifierToReplace) {
                  identifierToReplace.code = idt.code
                  identifierToReplace._status = 'ERROR'
                  identifierToReplace._error = T.identifier_error[error.errorCode]
                    ? __(T.identifier_error[error.errorCode])
                    : error.errorCode
                  identifierToReplace._errorCode = error.errorCode
                } else if (
                  idt.code !== referenceIdentifier.code &&
                  !encodingValidation.identifiers.some((idf) => idf.code === idt.code)
                ) {
                  encodingValidation.identifiers.push({
                    code: idt.code,
                    identifierType: idt.type,
                    role: idt.role,
                    _read: true,
                    _status: 'ERROR',
                    _error: T.identifier_error[error.errorCode]
                      ? __(T.identifier_error[error.errorCode])
                      : error.errorCode,
                    _errorCode: error.errorCode
                  })
                }
              })
            } else {
              // happy case: the backend set the extra tag as the one we've just read
              encodingValidation.identifiers.push({
                code: error.ref,
                identifierType: error.payload?.identifierType || 'UHF_TAG',
                role: error.payload?.role || '',
                _read: true,
                _status: 'ERROR',
                _error: T.identifier_error[error.errorCode] ? __(T.identifier_error[error.errorCode]) : error.errorCode,
                _errorCode: error.errorCode
              })
            }
            break
          }

          default: {
            // search by code
            let index = encodingValidation.identifiers.findIndex((idf) => idf.code === error.ref)
            if (isEncodingErrorCheckingByRole() && index === -1) {
              // search by role when tag not read
              index = encodingValidation.identifiers.findIndex((idf) => idf.role === error.ref)
            }
            if (index >= 0) {
              errorStr = T.identifier_error[error.errorCode] ? __(T.identifier_error[error.errorCode]) : error.errorCode
              encodingValidation.identifiers[index]._status = [
                'MISSING_MANDATORY_TAG',
                'MISSING_MANDATORY_TAG_DUAL'
              ].includes(error.errorCode)
                ? 'EXPECTED'
                : 'ERROR'
              if (!encodingValidation.identifiers[index]._error) {
                encodingValidation.identifiers[index]._error = errorStr
              } else {
                encodingValidation.identifiers[index]._error += ', ' + errorStr
              }
              if (error.payload?.errorMessage) {
                if (!encodingValidation.identifiers[index]._errorMessage) {
                  encodingValidation.identifiers[index]._errorMessage = error.payload?.errorMessage
                } else {
                  encodingValidation.identifiers[index]._errorMessage += ', ' + error.payload?.errorMessage
                }
              }
            }
            break
          }
        }
      })
    }
    return encodingValidation
  }

  static async validate(
    encodingValidation: EncodingValidationRequest,
    checkConfiguration = true,
    desiredCode = false,
    enabledUhfWrite = false
  ) {
    try {
      const knownRoleIdentifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: desiredCode ? idf.desiredCode : undefined
        }))

      // for unknown role identifiers, only one for each code is included in the request
      const unknownRoleIdentifiers: ConfigurationDetail[] = []
      encodingValidation.identifiers
        .filter((idf) => idf._possibleCodes?.length > 0)
        .forEach((idf) => {
          idf._possibleCodes.forEach((codeObj) => {
            if (!unknownRoleIdentifiers.find((unkRoleIdf) => unkRoleIdf.code === codeObj.code)) {
              unknownRoleIdentifiers.push({
                identifierType: idf.identifierType,
                code: codeObj.code,
                tid: codeObj.tid || ''
              })
            }
          })
        })

      const res = await Encodings.validate({
        ...encodingValidation,
        identifiers: [...knownRoleIdentifiers, ...unknownRoleIdentifiers]
      })
      if (res) {
        await this.set_identifiers_status(encodingValidation, res, enabledUhfWrite)
        if (checkConfiguration) {
          let configurationDetails
          if (res?.configurationDetails) configurationDetails = res.configurationDetails
          else if (res.configuration && res.configuration.details) configurationDetails = res.configuration.details
          if ((configurationDetails ?? []).length === 0) {
            this.throwNoConfigurationError()
          }
        }
        return { encodingValidationResponse: res, encodingValidation }
      } else {
        throw new Error(__(T.error.no_item_configuration))
      }
    } catch (error) {
      throw new Error((error as any)?.message ?? __(T.error.no_item_configuration))
    }
  }

  static throwNoConfigurationError(): never {
    throw new Error(__(T.error.no_item_configuration))
  }

  static getMixPanelData(
    operation: EncodingOperationConfig,
    encodingValidation?: EncodingValidationRequest,
    encodingValidationResponse?,
    seconds?,
    force?
  ) {
    const identifiers: any[] = encodingValidation ? this.getIdentifiers(encodingValidation!) : []

    const antennas: any = []
    if (
      AppStore.defaultWorkstation &&
      AppStore.defaultWorkstation.antennas &&
      AppStore.defaultWorkstation.antennas.length > 0
    ) {
      AppStore.defaultWorkstation.antennas.map((antenna) => {
        antennas.push({
          code: antenna.code,
          txPower: antenna.txPower,
          rxSensitivity: antenna.rxSensitivity,
          reader: {
            code: antenna.reader.code,
            deviceType: antenna.reader.deviceType,
            settings: antenna.reader.settings
          }
        })
      })
    }
    const mixpanelData: any = {
      operationCode: operation.code,
      operationDescription: operation.description,
      identifiers: identifiers,
      antennas: antennas,
      username: AppStore.loggedUser?.username ?? '',
      workstation: AppStore.defaultWorkstation ? AppStore.defaultWorkstation.code : '',
      place: AppStore.getWorkstationPlaceCode()
    }
    if (seconds) mixpanelData.time = seconds + ' seconds'
    if (encodingValidationResponse) {
      mixpanelData.operationToPerform = encodingValidationResponse.operationToPerform || ''
      mixpanelData.errors = encodingValidationResponse.errors || []
    }
    if (force) mixpanelData.force = force
    identifiers.map((idt) => {
      mixpanelData[idt.identifierType] = idt.code
    })
    return mixpanelData
  }

  static getIdentifiers(encodingValidation: EncodingValidationRequest) {
    if (encodingValidation && encodingValidation.identifiers) {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      return identifiers
    } else {
      return []
    }
  }

  static async create(encodingValidation: EncodingValidationRequest) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      const res = await Encodings.create({ ...encodingValidation, identifiers })
      return res
    } catch (error) {
      throw new Error(__(T.error.item_creation_error))
    }
  }

  static async force_create(encodingValidation: EncodingValidationRequest, pin?: string) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      const res = await Encodings.force_create({ ...encodingValidation, identifiers }, pin)
      return res
    } catch (error) {
      throw new Error(__(T.error.item_creation_error))
    }
  }

  static async onReplaceTagPressedExtension(encodingValidation: EncodingValidationRequest) {
    return true
  }

  static check_tag_is_logic_killable = (data) => {
    return false
  }

  static async verify(encodingValidation: EncodingValidationRequest, addCodesFromValidationResponse = false) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .filter((idf) => idf._read !== false)
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || ''
        }))
        .filter((idf) => !!idf.code)
      const res = await Encodings.verify({ ...encodingValidation, identifiers })
      if (res && res.item && res.item.identifiers) {
        let configurationDetails: ConfigurationDetail[] = []
        if (res.configuration && res.configuration.details) configurationDetails = res.configuration.details

        // add data for all encodingValidation.identifiers with code read
        encodingValidation.identifiers
          .filter((idf) => !!idf.code)
          .forEach((idf) => {
            const find = res.item.identifiers.find((idt) => idt.code === idf.code && idt.type === idf.identifierType)
            if (find) {
              idf.status = find.status ?? ''
              idf.group = find.group ?? ''
              idf.itemId = res.item.id ?? ''
              idf.role = find.role ?? ''
              idf.productionOrderRowId =
                find.productionOrderRowId && find.productionOrderRowId !== null ? find.productionOrderRowId : undefined
              idf.logicKillable = this.check_tag_is_logic_killable(find)
            }
          })

        // check configuration to add identifiers missing
        configurationDetails
          .filter((c) => !c.optional)
          .map((cd) => {
            const findConfiguration = encodingValidation.identifiers.find(
              (f) => f.identifierType === cd.identifierType && f.role === cd.role
            )
            if (!findConfiguration) {
              const identifierToAdd: ConfigurationDetail = {
                identifierType: cd.identifierType,
                role: cd.role ?? '',
                _status: 'TO_BE_READ'
              }
              if (addCodesFromValidationResponse) {
                const findIdentifier = res.item.identifiers.find(
                  (idt) => idt.type === cd.identifierType && idt.role === cd.role
                )
                if (findIdentifier) {
                  identifierToAdd.code = findIdentifier.code ?? ''
                }
              }
              encodingValidation.identifiers.push(identifierToAdd)
            }
          })

        // check tag associated with same group
        encodingValidation.identifiers
          .filter((idf) => !!idf.code)
          .forEach((idf) => {
            const find = res.item.identifiers.find((idt) => idt.code === idf.code && idt.type === idf.identifierType)
            if (find && !!find.group && !!find.code) {
              // find identifier in validate response for same group and differente code and type
              let groups = res.item.identifiers.filter((idt) => idt.group === find.group && idt.type !== find.type)
              if (groups.length === 0) {
                //search identifier with same group and type but different role
                groups = res.item.identifiers.filter(
                  (idt) => idt.group === find.group && idt.type === find.type && idt.role !== find.role
                )
              }

              if (groups.length > 0) {
                groups.forEach((group) => {
                  const check = encodingValidation.identifiers.find(
                    (c) => c.identifierType === group.type && c.role === group.role
                  )
                  if (group && (!check || (check && !check.code))) {
                    // find configuration for identifier with same identifierType and role
                    const findConfiguration = configurationDetails.find(
                      (f) => f.identifierType === group.type && f.role === group.role
                    )

                    // check if group exists
                    let exist
                    if (!findConfiguration?.readingRequired) {
                      //check if already exist same identifier read without error and not grouped
                      exist = encodingValidation.identifiers.find(
                        (idft) => idft.identifierType === group.type && idft.role === group.role
                      )
                      if (!exist) {
                        encodingValidation.identifiers.push({
                          identifierType: group.type,
                          role: group.role ?? '',
                          code: group.code ?? '',
                          status: group.status ?? '',
                          group: group.group ?? '',
                          itemId: res.item.id ?? '',
                          _read: false,
                          _status: group.type === 'SIMPLE_ITEM_IDENTIFIER' ? 'PROCESSING' : 'EXPECTED',
                          _error: undefined,
                          logicKillable: this.check_tag_is_logic_killable(group)
                        })
                      } else if (exist.code === undefined || exist.code === '') {
                        exist.code = group.code ?? ''
                        exist.status = group.status ?? ''
                        exist._status = group.type === 'SIMPLE_ITEM_IDENTIFIER' ? 'PROCESSING' : 'EXPECTED'
                        exist._read = false
                        exist._error = undefined
                        exist.group = group.group ?? ''
                        exist.itemId = res.item.id ?? ''
                        exist.logicKillable = this.check_tag_is_logic_killable(group)
                      }
                    }
                  }
                })
              }
            }
          })
      }

      // check and set backend errors
      res.errors.forEach((error) => {
        const errorCode = error.errorCode
        const isNotAssociatedTag = errorCode === 'Not Associated Tag'
        let errorMessage = T.identifier_error[error.errorCode]
          ? __(T.identifier_error[error.errorCode])
          : error.errorCode
        if (errorCode === 'Tag Mismatch') errorMessage = __(T.error.tags_mismatch_error)
        const index =
          isNotAssociatedTag || (addCodesFromValidationResponse && error.errorCode === 'MISSING_TAG')
            ? -1
            : encodingValidation.identifiers.findIndex((idf) => idf.code === error.ref)
        if (index >= 0) {
          if (error.payload) {
            encodingValidation.identifiers[index].group = error.payload.group ?? ''
            encodingValidation.identifiers[index].role = error.payload.role ?? ''
            encodingValidation.identifiers[index].logicKillable = this.check_tag_is_logic_killable(error.payload)
          }
          encodingValidation.identifiers[index]._status = 'ERROR'
          if (encodingValidation.identifiers[index]._errorCode)
            encodingValidation.identifiers[index]._errorCode += ',' + errorCode
          else encodingValidation.identifiers[index]._errorCode = errorCode
          encodingValidation.identifiers[index]._error = errorMessage
        } else if (isNotAssociatedTag && error.payload.identifierType !== 'SIMPLE_ITEM_IDENTIFIER') {
          const find = encodingValidation.identifiers.find((idt) => idt.code === error.ref)
          if (!find) {
            encodingValidation.identifiers.push({
              code: error.ref,
              ...error.payload,
              _read: false,
              _status: 'ERROR',
              _error: errorMessage,
              _errorCode: error.errorCode,
              _hidden: true,
              logicKillable: this.check_tag_is_logic_killable(error.payload)
            })
          } else {
            find.group = error.payload.group ?? ''
            find._status = 'ERROR'
            find._error = errorMessage
            find._errorCode = errorCode
            find.logicKillable = this.check_tag_is_logic_killable(error.payload)
          }
        }
      })

      //check MISSING_TAG errors
      res.errors
        .filter((e) => e.errorCode === 'MISSING_TAG')
        .forEach((error) => {
          const errorCode = error.errorCode
          const errorMessage = T.identifier_error[error.errorCode]
            ? __(T.identifier_error[error.errorCode])
            : error.errorCode
          const find = encodingValidation.identifiers.find(
            (idt) =>
              idt.identifierType === error.payload.identifierType &&
              idt.role === error.payload.role &&
              idt._errorCode !== 'Not Associated Tag'
          )
          if (find) {
            // find.code = error.ref
            if (find._status !== 'ERROR') find._status = 'EXPECTED'
            find._error = errorMessage
            find._errorCode = errorCode
            const findIdt = res.item.identifiers.find(
              (idt) => idt.code === find.code && idt.type === find.identifierType
            )
            if (findIdt) {
              find.status = findIdt.status ?? ''
              find.group = findIdt.group ?? ''
              find.itemId = res.item.id ?? ''
              find.role = findIdt.role ?? ''
              find.productionOrderRowId =
                findIdt.productionOrderRowId && findIdt.productionOrderRowId !== null
                  ? findIdt.productionOrderRowId
                  : undefined
              find.logicKillable = this.check_tag_is_logic_killable(findIdt)
            }
          } else if (error.payload.identifierType === 'SIMPLE_ITEM_IDENTIFIER') {
            encodingValidation.identifiers.push({
              code: error.ref,
              ...error.payload,
              _read: false,
              _status: 'EXPECTED',
              _error: errorMessage,
              _errorCode: error.errorCode
            })
          }
        })

      encodingValidation.identifiers
        .filter((idt) => !!idt.code && ['PROCESSING'].includes(idt._status))
        .map((idt) => {
          idt._status = 'CONFIRMED'
          idt._error = undefined
        })

      return { encodingValidationResponse: res, encodingValidation }
    } catch (error) {
      throw new Error((error as any).message ?? __(T.error.item_creation_error))
    }
  }

  static getErrorCode = (error) => {
    let errorCode: string = error.errorCode
    if (errorCode === 'Tag Mismatch') errorCode = __(T.error.tags_mismatch_error)
    return errorCode
  }

  static async onTagRead(
    encodingValidation: EncodingValidationRequest,
    _tag: TmrTag,
    operationConfig: EncodingOperationConfig,
    antennaId?: string,
    datadogSession?: DatadogEncodingRun
  ) {
    // define identifier type
    let identifierType = 'UHF_TAG'
    if (!!_tag.uid) identifierType = 'NFC_TAG'
    if (!!_tag.barcode) identifierType = 'SIMPLE_ITEM_IDENTIFIER'

    // create tag readed
    const tag = {
      code: _tag.epc ?? _tag.uid ?? _tag.barcode,
      tid: _tag.tid || '',
      identifierType
    } as { code: string; tid?: string; identifierType: IdentifierType }

    //search identifiers for regexFilter if exist
    let matchedRolesIdxs: number[] = []
    const identifierWithRegex = encodingValidation.identifiers.filter(
      (idt) => !!idt.regexFilter && idt.regexFilter !== 'sgtin'
    )
    if (identifierWithRegex.length > 0) {
      identifierWithRegex.forEach((idt, i) => {
        const match = tag.code.match(new RegExp(idt.regexFilter))
        if (match) {
          matchedRolesIdxs.push(i)
        }
      })
    }

    // pick all identifiers with same identifierType and no regexFilter or regexFilter is sgtin
    if (matchedRolesIdxs.length === 0) {
      matchedRolesIdxs = encodingValidation.identifiers.reduce((acc, idt, i) => {
        if ((!idt.regexFilter || idt.regexFilter === 'sgtin') && idt.identifierType === tag.identifierType) {
          acc.push(i)
        }
        return acc
      }, [])
    }

    const tagTid = tag.tid || ''
    const addProcessingIdentifier = () => {
      if (
        encodingValidation.identifiers.some((idt) => idt.code === tag.code && idt.identifierType === tag.identifierType)
      )
        return
      encodingValidation.identifiers.push({
        code: tag.code,
        tid: tagTid,
        identifierType: tag.identifierType,
        _status: 'PROCESSING',
        _added: true
      })
    }
    const setProcessingFlags = (identifier: ConfigurationDetail) => {
      identifier._status = 'PROCESSING'
      identifier._error = undefined
      identifier._read = true
      identifier._hidden = false
    }
    const pushPossibleCode = (identifier: ConfigurationDetail) => {
      if (!identifier._possibleCodes) {
        identifier._possibleCodes = []
      }
      identifier._possibleCodes.push({ code: tag.code, tid: tagTid })
    }

    if (matchedRolesIdxs.length > 0) {
      const onlyOneMatchingRoleIsFound = matchedRolesIdxs.length === 1
      // in case of not-univocal match with the role, the role attribution is demanded to the backend
      // and the code is set later
      matchedRolesIdxs.forEach((index) => {
        const identifier = encodingValidation.identifiers[index]
        const alreadyHasCode = !!identifier.code
        if (alreadyHasCode) {
          if (!identifier.role) {
            // the tag with the right role has been already read, so we add a new one
            addProcessingIdentifier()
          } else if (identifier._status === 'EXPECTED') {
            // encoding verify, after the first one is read
            const codeIsEqual = identifier.code === tag.code
            if (codeIsEqual) {
              setProcessingFlags(identifier)
              if (!onlyOneMatchingRoleIsFound) {
                pushPossibleCode(identifier)
              }
            } else {
              addProcessingIdentifier()
            }
          }
          return
        }

        // just update the identifier box
        identifier.code = onlyOneMatchingRoleIsFound ? tag.code : undefined
        identifier.tid = onlyOneMatchingRoleIsFound ? tagTid : undefined
        setProcessingFlags(identifier)
        if (!onlyOneMatchingRoleIsFound) {
          pushPossibleCode(identifier)
        }
      })
    } else {
      addProcessingIdentifier()
    }
  }

  static resetIdentifiersEncodingValition(
    operation: EncodingOperationConfig,
    encodingValidationResponse?: EncodingValidationResponse,
    encodingValidation?: EncodingValidationRequest
  ) {
    const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(operation)
    const identifiers: ConfigurationDetail[] = []
    if (encodingValidationResponse && encodingValidationResponse.configuration) {
      let configurationDetails
      if (encodingValidationResponse.configurationDetails)
        configurationDetails = encodingValidationResponse.configurationDetails
      else if (encodingValidationResponse.configuration && encodingValidationResponse.configuration.details)
        configurationDetails = encodingValidationResponse.configuration.details
      if (!configurationDetails) configurationDetails = []

      configurationDetails.forEach((configDetail) => {
        if (!expectOnlyMandatoryIdentifiers || (expectOnlyMandatoryIdentifiers && !configDetail.optional)) {
          identifiers.push({ ...configDetail })
        }
      })
    }
    return identifiers
  }

  static getOptionEncodingPage(value, encodingConfig: EncodingOperationConfig) {
    switch (value) {
      case 'associate':
        Router.navigate(`/encoding/:opCode`, { opCode: encodingConfig.code })
        break
      case 'verify':
        Router.navigate(`/encoding/:opCode/verify`, { opCode: encodingConfig.code })
        break
      default:
        Router.navigate(`/encoding/:opCode`, { opCode: encodingConfig.code })
        break
    }
  }

  static sortIdentifiers(identifiers: ConfigurationDetail[]) {
    //Sort identifiers with first the identifierType as SIMPLEITEMIDENTIFIER and then the others
    return identifiers.sort((a, b) => {
      if (a.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && a._status !== 'CONFIRMED') {
        return -1
      } else if (b.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && b._status !== 'CONFIRMED') {
        return 1
      }
      return 0
    })
  }

  static getEncodingProduct = (encodingValidationResponse: EncodingValidationResponse | undefined) => {
    let product
    if (encodingValidationResponse && encodingValidationResponse.item) {
      product = encodingValidationResponse.item.product
      if (
        enabledWamGroupedProduct() &&
        encodingValidationResponse.item.attributes &&
        encodingValidationResponse.item.attributes.wam
      ) {
        if (!product.attributes) {
          product.attributes = {}
        }
        product.attributes.wam = encodingValidationResponse.item.attributes.wam
      }
    }
    return product
  }

  static getProductionOrderFields = (_operation?: EncodingOperationConfig) => [
    { label: __(T.misc.order_type), path: 'orderType' }
  ]

  static getOverrideProductInfoFields = (
    _context: OverrideProductInfoFieldsContext,
    _operation: EncodingOperationConfig
  ) => null

  static getOrderedProductionOrderRows = (productionOrderRows: ProductionOrderRowDetail[]) => {
    return productionOrderRows
  }

  static getProductionOrderDetails = ({
    productionOrderRow
  }: GetProductionOrderDetails): {
    subtitle: string
    data: ProductionOrder | ProductionOrderRow | undefined
  } => {
    return { subtitle: productionOrderRow?.productionOrder?.code, data: productionOrderRow?.productionOrder }
  }
}
